/* eslint-disable @typescript-eslint/no-explicit-any */
import { VisSingleContainer, VisTooltip, VisTopoJSONMap } from '@unovis/react';
import { MapProjection, TopoJSONMap } from '@unovis/ts';
import type { AreaDatum } from '@unovis/ts/components/area/types';
import type { VisEventType } from '@unovis/ts/core/component/types';
import { WorldMapTopoJSON } from '@unovis/ts/maps';
import type { NextPageContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { NavFooter } from 'src/components/nav/navFooter.component';
import { searchForAllCountriesStaticProps } from '../actions/searchActions';
import StandardSEOWrapper from '../components/layout/standardSEOWrapper.component';

interface Country {
  id: string;
  countryName: string;
  certaintyIndexColor: string;
  completionIndexColor: string;
  recordedIndexColor: string;
  certainty_index: number;
  completion_index: number;
  recorded_index: number;
}

type WorldMapProps = {
  mapCountries: Country[];
};

function getGradientColor(percentage: number) {
  type GradientColor = {
    stop: number;
    color: [number, number, number]; // RGB tuple
  };

  const colors: GradientColor[] = [
    { stop: 0, color: [184, 246, 229] }, // #B8F6E5
    { stop: 50, color: [102, 203, 175] }, // #66CBAF
    { stop: 100, color: [64, 159, 133] }, // #409F85
  ];

  if (percentage === 0) {
    return '#e4e4e7';
  }

  // Ensure percentage is within bounds
  percentage = Math.max(0, Math.min(percentage, 100));

  // Find surrounding colors
  let start: GradientColor | undefined, end: GradientColor | undefined;
  for (let i = 0; i < colors.length - 1; i++) {
    if (percentage >= colors[i].stop && percentage <= colors[i + 1].stop) {
      start = colors[i];
      end = colors[i + 1];
      break;
    }
  }

  if (!start || !end) return '#B8F6E5'; // Default to first color if not found

  // Calculate interpolation factor
  const factor = (percentage - start.stop) / (end.stop - start.stop);

  // Interpolate between start and end colors
  const interpolatedColor = start.color.map((startVal, index) =>
    Math.round(
      startVal + factor * ((end?.color?.[index] ?? startVal) - startVal)
    )
  );

  // Convert RGB to Hex
  return `#${interpolatedColor
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;
}

export default function WorldMap({ mapCountries }: Readonly<WorldMapProps>) {
  const { t } = useTranslation(['world']);

  const router = useRouter();
  const [category, setCategory] = useState<string>('Recorded');
  const [canMapScroll, setCanMapScroll] = useState<boolean>(false);

  const triggers = {
    [TopoJSONMap.selectors.feature]: (d: any) => {
      const fieldsMappings: Record<string, number> = {
        Recorded: d.data?.recorded_index || 0,
        Complete: d.data?.completion_index || 0,
        Certain: d.data?.certainty_index || 0,
      };

      return `<div class="!max-w-[130px] flex flex-col gap-2 p-3"><p class="text-zinc-950 text-xs font-semibold">${
        d.data.countryName || d.properties.name
      }</p><div class="flex gap-2"><p class="text-xs font-normal">${category}</p><p class="ml-auto text-xs font-semibold">${
        fieldsMappings[category]
      }%</p></div></div>`;
    },
  };

  const mapCountryColorsToCategory = () => {
    const fieldsMappings: Record<string, string> = {
      Recorded: 'recordedIndexColor',
      Complete: 'completionIndexColor',
      Certain: 'certaintyIndexColor',
    };

    return mapCountries.map((country: any) => ({
      ...country,
      color: country[fieldsMappings[category]],
      outlineColor: '#000',
    }));
  };

  return (
    <div className={`flex min-h-screen flex-col bg-[#FAFAFA]`}>
      <StandardSEOWrapper
        title={t('world:seo.title')}
        description={t('world:seo.description')}
      />

      <div className="mx-5 flex flex-col gap-2 pb-6 pt-10 text-gray-950 max-md:bg-[#FAFAFA] md:mx-[80px] md:pb-[40px] md:pt-[60px] xl:gap-4">
        <p className="text-2xl font-semibold text-zinc-950 md:text-[40px]">
          World map
        </p>
        <p className="text-sm font-normal text-zinc-600 md:text-lg">
          Interactive map with all updated records for each country
        </p>
      </div>

      <div className="mx-5 mb-5 flex flex-col gap-6 rounded-xl bg-white p-5 md:p-8 shadow-[0px_2px_16px_-4px_rgba(9,9,11,0.08)] md:mx-[80px] md:mb-[60px]">
        <div className="inline-flex h-9 items-center justify-start gap-1 overflow-hidden rounded-md bg-zinc-100 p-1 md:mr-auto">
          <button
            onClick={() => setCategory('Recorded')}
            className={`flex h-7 w-full items-center justify-center rounded px-3 py-2 text-xs font-medium leading-none text-zinc-500 md:w-20 ${
              category === 'Recorded'
                ? 'bg-white text-zinc-950 shadow-[0px_2px_10px_-4px_rgba(9,9,11,0.20)]'
                : ''
            }`}
          >
            Recorded
          </button>
          <button
            onClick={() => setCategory('Complete')}
            className={`flex h-7 w-full items-center justify-center rounded px-3 py-2 text-xs font-medium leading-none text-zinc-500 md:w-20 ${
              category === 'Complete'
                ? 'bg-white text-zinc-950 shadow-[0px_2px_10px_-4px_rgba(9,9,11,0.20)]'
                : ''
            }`}
          >
            Complete
          </button>
          <button
            onClick={() => setCategory('Certain')}
            className={`flex h-7 w-full items-center justify-center rounded px-3 py-2 text-xs font-medium leading-none text-zinc-500 md:w-20 ${
              category === 'Certain'
                ? 'bg-white text-zinc-950 shadow-[0px_2px_10px_-4px_rgba(9,9,11,0.20)]'
                : ''
            }`}
          >
            Certain
          </button>
        </div>
        <div className="relative">
          {canMapScroll && (
            <button
              onClick={() => setCanMapScroll(false)}
              className="absolute top-2 right-2 z-20 rounded bg-white px-3 py-1 text-xs shadow-md md:hidden"
            >
              Disable map interaction
            </button>
          )}
          {!canMapScroll && (
            <button
              onClick={() => setCanMapScroll(true)}
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 text-center px-4 text-zinc-700 text-sm font-medium md:hidden"
            >
              Double tap to interact with the map
            </button>
          )}
          <div
            className={`${
              !canMapScroll ? 'pointer-events-none md:pointer-events-auto' : ''
            }`}
          >
            <VisSingleContainer
              key={canMapScroll ? 'interactive' : 'locked'}
              className="max-h-[400px] min-h-[400px] md:min-h-[700px] md:max-h-full"
              data={{ areas: mapCountryColorsToCategory() }}
            >
              <VisTooltip
                className="!rounded !p-0 !shadow-[0px_8px_16px_-5px_rgba(15,25,40,0.20)] !outline-[0.50px] !outline-zinc-200"
                triggers={triggers}
              />
              <VisTopoJSONMap
                events={{
                  [TopoJSONMap.selectors.background]: {
                    wheel: (data: AreaDatum, event: MouseEvent) => {
                      if (!canMapScroll) {
                        event.stopPropagation();
                      }
                    },
                    click: () => {
                      setCanMapScroll(true);
                    },
                  },
                  [TopoJSONMap.selectors.feature]: {
                    wheel: (data: AreaDatum, event: MouseEvent) => {
                      if (!canMapScroll) {
                        event.stopPropagation();
                      }
                    },
                    ['mouseenter' as VisEventType]: (
                      data: AreaDatum,
                      event: MouseEvent,
                      i: number,
                      elements: SVGElement[]
                    ) => {
                      elements[i].style.stroke = '#34A28A';
                      elements[i].style.strokeWidth = '2px';
                      elements[i].style.cursor = 'pointer';
                    },
                    ['mouseleave' as VisEventType]: (
                      data: AreaDatum,
                      event: MouseEvent,
                      i: number,
                      elements: SVGElement[]
                    ) => {
                      elements[i].style.stroke = '';
                      elements[i].style.strokeWidth = '';
                      elements[i].style.cursor = 'auto';
                    },
                    click: (country: any) => {
                      setCanMapScroll(true);
                      if (country?.data?.from4D) {
                        router.push({
                          pathname: `/country/${country.data.countryName
                            .replaceAll(' ', '-')
                            .toLowerCase()}`,
                        });
                      }
                    },
                  },
                }}
                projection={MapProjection.Mercator()}
                topojson={WorldMapTopoJSON}
              />
            </VisSingleContainer>
          </div>
          <div className="inline-flex items-center justify-end gap-2">
            <p className="justify-start text-xs font-normal leading-none text-zinc-600">
              0
            </p>
            <div className="h-1.5 w-40 bg-gradient-to-l from-[#409f85] via-[#66caaf] to-[#b7f6e5]" />
            <p className="justify-start text-xs font-normal leading-none text-zinc-600">
              100
            </p>
          </div>
        </div>
      </div>

      <NavFooter />
    </div>
  );
}

export const getServerSideProps = async ({ locale }: NextPageContext) => {
  const translations = await serverSideTranslations(locale ?? 'en', [
    'common',
    'about',
    'partners',
    'navbar',
    'new-homepage',
    'new-navbar',
    'new-footer',
    'world',
  ]);

  const countriesStaticProps = await searchForAllCountriesStaticProps();
  const mapCountries = WorldMapTopoJSON.objects.countries.geometries
    .filter((country: any) => country.iso)
    .map((country: any) => {
      const staticCountry = countriesStaticProps.find(
        (staticCountry) => staticCountry.iso_2 === country.iso
      );
      return {
        id: country.iso,
        countryName: staticCountry?.name || '',
        from4D: !!staticCountry,
        certaintyIndexColor: getGradientColor(
          staticCountry?.certainty_index || 0
        ),
        completionIndexColor: getGradientColor(
          staticCountry?.completion_index || 0
        ),
        recordedIndexColor: getGradientColor(
          staticCountry?.record_count && staticCountry?.population
            ? (staticCountry.record_count / staticCountry.population) * 100
            : 0
        ),
        certainty_index: staticCountry?.certainty_index || 0,
        completion_index: staticCountry?.completion_index || 0,
        recorded_index: Math.min(
          99.9,
          +(
            staticCountry?.record_count && staticCountry?.population
              ? (staticCountry.record_count / staticCountry.population) * 100
              : 0
          ).toFixed(2)
        ),
      };
    });

  return {
    props: {
      ...(translations && typeof translations === 'object' ? translations : {}),
      mapCountries,
      countries: countriesStaticProps,
    },
  };
};
